import { History, Location } from "history";
import { ApiService, downloadFile, getFilenameFromHeaders } from "../api";
import UserStore from "./UserStore";
import SearchController from "./SearchController";
import ShareholdersStore from "./ShareholdersStore";
import ShareTypesStore from "./ShareTypesStore";
import ArticleStore from "./ArticleStore";
import AlertMessageStore from "./AlertMessageStore";
import { AxiosInstance } from "axios";
import { DialogStore, DialogType } from "../dialogs/dialogDomain";
import PortfolioStore, { getCompaniesWithAdminAccessType } from "./PortfolioStore";
import { DownloadService } from "../api/DownloadService";
import { AffiliateService } from "../affiliate/Affiliate";
import { initUserPrefs, UserPrefs } from "./userPrefsDomain";
import { WebSocketStore } from "../webSocketDomain";
import { makeAutoObservable } from "mobx";
import { SignDocumentPageState } from "../signing/signDocumentDomain";
import {
  createEventDispatcher,
  DCEvent,
  handleHttpEvent,
  handleLoadUrlEvent,
  handleLogErrorEvent,
  handleNavigateEvent,
  handleNavigateToEvent,
  handleReloadEvent,
  handleShowAlertEvent,
  handleUpdateStoreEvent,
  OrderLiveGeneralMeetingEvent,
  showAlert,
} from "../events/eventsDomain";
import { I18nState, initialI18nState } from "../i18n/i18nDomain";
import { PortfolioPageState, PortfolioSignaturesState } from "../portfolio/portfolioDomain";
import { ScheduleState } from "../schedule/scheduleDomain";
import { loadProject } from "../dbot/project/ProjectInitializerController";
import { AccessType } from "../types/enums/AccessType";
import { FollowUpsState } from "../backoffice/backofficeDomain";
import {
  BeneficialOwnersApiClient,
  BeneficialOwnersStore,
} from "../shareholderRegister/beneficialOwners/beneficialOwnersDomain";
import { CompanySettingsState } from "../overview/companySettings/companySettingsDomain";
import { RootStoreData } from "../events/rootStoreData";
import { TransactionStore } from "./TransactionStore";
import { MatchedDCRoute } from "../routes/routes";
import { logDebug } from "../utils/log";
import { AdminSearchPageState } from "../search/adminSearchDomain";
import { AnnouncementDTO } from "../announcement/announcementDomain";
import { validateFiles } from "../fileUpload/fileUploadDomain";
import { DocumentForViewingState } from "../document/documentApi";
import { when } from "../utils/functions";
import { trimStart } from "lodash";
import { DbotState } from "../dbot/projectList/v2/domain";
import { PowerOfAttorneyState } from "../overview/powerOfAttorney/powerOfAttorneyDomain";
import { ArticlesOfAssociationState } from "../overview/ArticlesOfAssociation  /articlesOfAssociationDomain";
import { createFileTypeValidationErrorMessage } from "../fileUpload/fileUploadErrorMessages";

export class RootStore implements RootStoreData {
  http: AxiosInstance;
  apiService: ApiService;
  history: History;
  userStore: UserStore;
  messageStore: AlertMessageStore;
  searchStore: SearchController;
  shareholdersStore: ShareholdersStore;
  transactionStore: TransactionStore;
  shareTypesStore: ShareTypesStore;
  articleStore: ArticleStore;
  dialogStore: DialogStore;
  portfolioStore: PortfolioStore;
  affiliateService: AffiliateService;
  webSocketStore?: WebSocketStore;
  beneficialOwnersStore: BeneficialOwnersStore;
  signDocument?: SignDocumentPageState;
  i18n: I18nState;
  userPrefs: UserPrefs;
  portfolioState?: PortfolioPageState;
  portfolioSignatures?: PortfolioSignaturesState;
  schedule?: ScheduleState;
  followUps?: FollowUpsState;
  companySettings?: CompanySettingsState;
  dialogs?: DialogType[];
  route?: MatchedDCRoute;
  adminSearch?: AdminSearchPageState;
  announcement?: AnnouncementDTO;
  documentForViewing?: DocumentForViewingState;
  dbot?: DbotState;
  powerOfAttorney?: PowerOfAttorneyState;
  articlesOfAssociation?: ArticlesOfAssociationState;
  timeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();

  constructor(http: AxiosInstance, apiService: ApiService, history: History) {
    this.http = http;
    this.apiService = apiService;
    this.history = history;
    this.userStore = new UserStore(apiService, history, this.dispatch);
    this.messageStore = new AlertMessageStore();
    this.dialogStore = new DialogStore(
      this.dispatch,
      this.userStore,
      this.apiService,
      history,
      this.http,
      this.messageStore,
    );
    this.searchStore = new SearchController(apiService);
    this.shareholdersStore = new ShareholdersStore(
      history,
      apiService,
      http,
      this.messageStore,
      this.userStore,
      this.dialogStore,
    );
    this.transactionStore = new TransactionStore(
      apiService,
      this.userStore,
      this.messageStore,
      history,
    );
    this.portfolioStore = new PortfolioStore(
      this.userStore,
      this.shareholdersStore,
      this.dialogStore,
      history,
      this.http,
    );
    const downloadService = new DownloadService(this.http);
    this.shareTypesStore = new ShareTypesStore(
      apiService,
      this.userStore,
      this.shareholdersStore,
      downloadService,
      this.messageStore,
    );
    this.articleStore = new ArticleStore(apiService, this.userStore);
    this.affiliateService = new AffiliateService(http);
    this.beneficialOwnersStore = new BeneficialOwnersStore(
      new BeneficialOwnersApiClient(http),
      downloadService,
      this.messageStore,
      this.userStore,
      this.dispatch,
    );
    this.userPrefs = initUserPrefs();
    this.i18n = initialI18nState();
    makeAutoObservable(this, { http: false, history: false }, { autoBind: true });
  }

  handleEvent = (event: DCEvent, _: number, context: DCEvent[]) => {
    switch (event.type) {
      case "showAcceptCompanyTermsDialog":
        this.dialogStore.openAcceptCompanyTermsDialog();
        break;
      case "showPaymentDialog":
        this.dialogStore.openSubscriptionDialog(
          this.i18n.lang,
          this.userStore,
          this.affiliateService,
        );
        break;
      case "showCreatePortfolioDialog":
        this.dialogStore.openCreatePortfolioDialog();
        break;
      case "showSendInvitationDialog":
        this.dialogStore.openSendInvitationDialog(event.data);
        break;
      case "showInviteNewAdminDialog":
        this.dialogStore.openInviteNewAdminDialog({
          lang: event.lang ?? this.i18n.lang,
          companies: getCompaniesWithAdminAccessType(this.portfolioStore.allCompanies),
          companyItems: event.companyItems,
        });
        break;
      case "showGeneralMeetingAnnouncementDialog":
        this.dialogStore.openGeneralMeetingReleaseDialog();
        break;
      case "showEditEmailDialog":
        this.portfolioStore.openEditEmailDialog({
          id: event.externalId,
          email: event.email,
          onSuccess: event.onSuccess,
          name: event.name,
          customRoleId: event.customRoleId,
        });
        break;
      case "showRevokeAllDialog":
        this.portfolioStore.openRevokeAllDialog({
          orgNos: event.orgNos,
          externalId: event.externalId,
          name: event.name,
          onSuccess: event.onSuccess,
          portfolio: event.portfolio,
        });
        break;
      case "preview-file":
        this.dialogStore.openDialog({
          type: "preview-file",
          file: event.file,
          filename: event.filename,
        });
        break;
      case "new-meeting":
        this.dialogStore.openNewMeetingDialog(
          event.orgNo,
          event.meetingType,
          event.projectName,
          event.projectId,
        );
        break;
      case "editProjectName":
        this.dialogStore.openDialog({
          type: "editProjectName",
          name: event.name,
          save: event.save,
          isDuplicate: event.isDuplicate,
        });
        break;
      case "showChangePermissionDialog":
        this.dialogStore.openChangePermissionDialog(event.title, event.objectId, event.onUpdate);
        break;
      case "closeDialog":
        this.dialogs?.pop();
        break;
      case "subscribe-to-web-socket-messages":
        this.createWebSocketStore().subscribe(event.topic, event.onMessage, event.onSubscribed);
        break;
      case "unsubscribe-from-web-socket-messages":
        event.topics.forEach((topic) => this.createWebSocketStore().unsubscribe(topic));
        break;
      case "load-url":
        handleLoadUrlEvent(event);
        break;

      case "show-alert":
        handleShowAlertEvent(this.messageStore, event);
        break;

      case "reload":
        handleReloadEvent();
        break;

      case "http":
        handleHttpEvent(this.http, this.dispatch, event, this);
        break;

      case "update-store":
        handleUpdateStoreEvent(this, event);
        break;

      case "update-local-storage":
        localStorage.setItem(event.key, JSON.stringify(event.val));
        break;

      case "update-session-storage":
        sessionStorage.setItem(event.key, event.val);
        break;

      case "navigate":
        handleNavigateEvent(this.history, event);
        break;

      case "navigate-to":
        handleNavigateToEvent(this.history, event);
        break;

      case "log-error":
        handleLogErrorEvent(event, context);
        break;

      case "with-store":
        this.dispatch(event.events(this));
        break;

      case "order-live-general-meeting":
        this.handleOrderGeneralMeeting(event);
        break;

      case "load-project":
        if (this.userStore.user && this.userStore.companyInfo) {
          loadProject(
            this.http,
            this.history,
            this.userStore.user,
            this.userStore.companyInfo,
            event.projectId,
            AccessType.ADMIN,
            this.dispatch,
            this,
            this.userStore.companies,
          )
            .then((ctrl) => this.dispatch(event.onSuccess(ctrl)))
            .catch((err) => this.dispatch(event.onFailure(err)));
        }
        break;

      case "show-dialog":
        if (!this.dialogs) {
          this.dialogs = [];
        }
        this.dialogs.push(event.dialog);
        break;

      case "thunk":
        event.thunk?.(this.dispatch);
        break;

      case "copy-to-clipboard":
        navigator.clipboard
          .writeText(event.text)
          .then(() => this.dispatch([showAlert({ key: "general.copied-to-clipboard" }, "info")]));
        break;

      case "fetchTransactionLog":
        this.apiService
          .fetchTransactionLog(event.orgNo)
          .then((transactions) => event.onSuccess(transactions))
          .catch(() => this.messageStore.showError({ key: "shareholders.error.load" }));
        break;

      case "validate-files":
        validateFiles(event.files, event.fileUploadConfig)
          .then(() => this.dispatch(event.onSuccess))
          .catch((err) =>
            this.dispatch(event.onFailure(createFileTypeValidationErrorMessage(err), err)),
          );
        break;

      case "download-file":
        this.http
          .get(trimStart(event.url, "/api"), { headers: { Accept: "*/*" }, responseType: "blob" })
          .then((res) => {
            downloadFile(res.data, getFilenameFromHeaders(res.headers));
            when(event.onSuccess, this.dispatch);
          })
          .catch(() => {
            when(event.onFailure, this.dispatch);
          });
        break;

      case "update-company-info":
        this.userStore.updateCompanyInfo();
        break;

      case "set-timeout":
        clearTimeout(event.timeoutId);
        this.timeouts.set(
          event.timeoutId,
          setTimeout(() => {
            this.timeouts.delete(event.timeoutId);
            this.dispatch(event.onTimeout);
          }, event.timeoutMs),
        );
        break;

      case "clear-timeout":
        clearTimeout(event.timeoutId);
        break;
    }
  };

  dispatch = createEventDispatcher(this.handleEvent);

  private createWebSocketStore() {
    if (!this.webSocketStore) {
      this.webSocketStore = new WebSocketStore(this.userStore.user);
    }
    return this.webSocketStore;
  }

  handleOrderGeneralMeeting = (event: OrderLiveGeneralMeetingEvent) => {
    this.http
      .post<void>(
        `/project/${event.orgNo}/order-live-general-meeting?generalMeetingDate=${event.generalMeetingDate}${event.projectId ? "&projectId=" + event.projectId : ""}`,
        {},
      )
      .then(event.onSuccess)
      .catch(event.onFailure);
  };

  setRoute(route: MatchedDCRoute | undefined) {
    logDebug("RootStore::setRoute", route);
    this.route = route;
  }

  get location(): Location {
    if (this.route) {
      // Note: need to dereference `route` so that observers of `location` gets notified.
      // This is because `history` and `location` are not observed by this store, as that
      // messes up the history object. But we know that `route` changes whenever the
      // history changes, so we can safely use that as a reference.
    }
    return this.history.location;
  }

  get locationStr(): string {
    if (this.route) {
      // See above comment. Same goes here.
    }
    return this.location.pathname + this.location.search;
  }
}
